/**
 * Procurement savings calculator (P2P ROI calculator).
 *
 * All coefficients, modifiers and formulas come from the
 * "Procurement Savings Calculator — Technical Specification".
 * Do not change the numbers below without updating that document.
 */

// Employees in the company → organizational complexity modifier
const ORG_COMPLEXITY = {
  '50-100': 0.9,
  '101-200': 1.0,
  '201-500': 1.1,
  '501-1000': 1.2,
  '1001-2500': 1.3,
  '2501-5000': 1.4,
  '5000+': 1.5,
};

// Locations, legal entities, or business units → purchasing fragmentation modifier
const FRAGMENTATION = {
  '1-5': 0.9,
  '6-20': 1.0,
  '21-50': 1.1,
  '51-100': 1.2,
  '100+': 1.3,
};

// Current purchasing process → process coefficient
const PROCESS_COEFFICIENT = {
  manual: 1.25,
  erp: 1.1,
  disconnected: 1.15,
  basic: 0.95,
  automated: 0.75,
};

// Current purchasing process → invoice processing savings rate, USD per invoice
const INVOICE_SAVINGS_RATE = {
  manual: { min: 7.0, max: 10.0 },
  erp: { min: 6.0, max: 8.5 },
  disconnected: { min: 6.0, max: 9.0 },
  basic: { min: 5.0, max: 7.5 },
  automated: { min: 3.0, max: 5.0 },
};

// Current purchasing process → estimated invoice exception rate
const EXCEPTION_RATE = {
  manual: 0.22,
  erp: 0.18,
  disconnected: 0.2,
  basic: 0.15,
  automated: 0.1,
};

// Category savings rates as a share of annual addressable spend
const SPEND_RATES = {
  costAvoidance: { min: 0.004, max: 0.012 },
  consolidation: { min: 0.0025, max: 0.008 },
  policy: { min: 0.0035, max: 0.01 },
};

const COST_PER_PO = 34; // USD, midpoint of the $14–$54 benchmark range
const COST_PER_EXCEPTION = 50; // USD, direct cost of investigating one exception
const PO_EFFICIENCY = { min: 0.2, max: 0.4 }; // purchasing efficiency improvement
const EXCEPTION_PREVENTION = { min: 0.25, max: 0.4 }; // share of exceptions prevented

// Guardrails: caps as a share of annual addressable spend
const CAPS = {
  cycles: { min: 0.03, max: 0.05 },
  invoices: { min: 0.02, max: 0.04 },
  total: { min: 0.08, max: 0.1 },
};

// Order is load-bearing: §4.8 rounds the first five displayed categories to the cent and
// derives the last one as the remainder, so the six always add up to the displayed total.
const CATEGORY_ORDER = ['costAvoidance', 'consolidation', 'policy', 'cycles', 'invoices', 'fraud'];

const RESULT_FIELDS = ['total', ...CATEGORY_ORDER];

const SCENARIOS = ['min', 'max'];

const MAX_DIGITS = 15; // keeps typed amounts inside the safe-integer range

const clamp = (value, min, max) => Math.max(Math.min(value, max), min);

// Own-property lookup only: a key like "toString" must miss, not resolve up the prototype
// chain and slip past the "is this input filled in?" check below.
const lookup = (table, key) =>
  typeof key === 'string' && Object.prototype.hasOwnProperty.call(table, key)
    ? table[key]
    : undefined;

const toAmount = value => (Number.isFinite(value) && value > 0 ? value : 0);

const toCents = value => Math.round(value * 100);

const emptyResults = () =>
  SCENARIOS.reduce(
    (acc, scenario) => {
      const zeros = RESULT_FIELDS.reduce((fields, field) => ({ ...fields, [field]: 0 }), {});
      return {
        ...acc,
        [scenario]: zeros,
        raw: { ...acc.raw, [scenario]: zeros },
        scale: { ...acc.scale, [scenario]: 1 },
      };
    },
    { raw: {}, scale: {} },
  );

/**
 * Splits a total, in whole cents, across `values` in proportion to them.
 *
 * Every value is floored to a cent first, then the cents left over go to the values with the
 * largest fractions — the standard largest-remainder method. It keeps three properties the
 * results page needs at once: the parts always add up to the total exactly, no part drifts
 * more than a cent from its proportional share, and a value of zero stays zero.
 *
 * §4.8 words this as "round the first five, derive the sixth as the remainder". That reads the
 * same in the common case, but it hands the entire rounding residue to one fixed category, and
 * that category is Fraud & payment risk prevention — which is zero whenever the user reports
 * no invoices. On small totals it would print cents there anyway, and could even leave the
 * minimum scenario above the maximum. This satisfies the same stated guarantee without that.
 */
const apportionCents = (values, totalCents) => {
  const floors = values.map(value => Math.floor(value * 100));
  const byFraction = values
    .map((value, index) => ({ index, fraction: value * 100 - floors[index] }))
    .sort((a, b) => b.fraction - a.fraction || a.index - b.index);

  const cents = [...floors];
  let left = totalCents - floors.reduce((sum, value) => sum + value, 0);

  for (const { index } of byFraction) {
    if (left <= 0) break;
    cents[index] += 1;
    left -= 1;
  }

  // Flooring can also overshoot, when the total itself rounded down. Take those cents back
  // from the smallest fractions, and never below zero.
  for (const { index } of [...byFraction].reverse()) {
    if (left >= 0) break;
    if (cents[index] === 0) continue;
    cents[index] -= 1;
    left += 1;
  }

  return cents.map(value => value / 100);
};

/**
 * §4.8 Category display scaling. Each category is calculated independently, so their sum can
 * exceed the 8%/10% total guardrail. Left alone, the six cards would add up to more than the
 * total on screen and read as a mistake, so once the guardrail bites the breakdown is scaled
 * by the same factor. Proportions are preserved; the mix is deliberately not rebalanced.
 *
 * @param {Object<string, number>} raw category values from §4.1–§4.6
 * @param {number} cap the scenario's share of annual addressable spend
 */
const scaleForDisplay = (raw, cap) => {
  const rawSum = CATEGORY_ORDER.reduce((sum, field) => sum + raw[field], 0);
  const cappedTotal = Math.min(rawSum, cap);
  const scale = rawSum > cappedTotal && rawSum > 0 ? cappedTotal / rawSum : 1;

  const totalCents = toCents(cappedTotal);
  const cents = apportionCents(
    CATEGORY_ORDER.map(field => raw[field] * scale),
    totalCents,
  );

  const displayed = { total: totalCents / 100 };
  CATEGORY_ORDER.forEach((field, index) => {
    displayed[field] = cents[index];
  });

  return { displayed, scale };
};

/**
 * Turns the user input into the six savings categories and the capped total,
 * for both the minimum and the maximum scenario.
 *
 * @param {{spend: number, orders: number, invoices: number, employees: string,
 *          locations: string, process: string}} state
 * Returns the values to display — already scaled and reconciled per §4.8 — alongside the
 * untouched raw category values, which §4.8 requires be kept for analytics and debugging.
 *
 * @returns {{min: Object<string, number>, max: Object<string, number>,
 *            raw: {min: Object<string, number>, max: Object<string, number>},
 *            scale: {min: number, max: number}}}
 */
export const calculate = state => {
  const input = state || {};
  const org = lookup(ORG_COMPLEXITY, input.employees);
  const fragmentation = lookup(FRAGMENTATION, input.locations);
  const process = lookup(PROCESS_COEFFICIENT, input.process);
  const spend = toAmount(input.spend);

  // The spec marks all six inputs required, and annual addressable spend must be > 0:
  // it is the base of every guardrail, so without it the model has nothing to cap against
  // and a category could otherwise print a value above a $0.00 total.
  if (!org || !fragmentation || !process || spend <= 0) return emptyResults();

  const orders = toAmount(input.orders);
  const invoices = toAmount(input.invoices);

  // Derived coefficient, used by the three spend-leakage categories.
  const spendControl = clamp(org * fragmentation * process, 1.0, 1.6);
  const cyclesCoefficient = clamp(org * process, 0.7, 1.5);
  const fraudCoefficient = clamp(fragmentation * process, 0.7, 1.4);
  const invoiceRate = INVOICE_SAVINGS_RATE[input.process];
  const exceptionRate = EXCEPTION_RATE[input.process];

  return SCENARIOS.reduce(
    (acc, scenario) => {
      const categories = {
        costAvoidance: spend * SPEND_RATES.costAvoidance[scenario] * spendControl,
        consolidation: spend * SPEND_RATES.consolidation[scenario] * spendControl,
        policy: spend * SPEND_RATES.policy[scenario] * spendControl,
        cycles: Math.min(
          orders * COST_PER_PO * PO_EFFICIENCY[scenario] * cyclesCoefficient,
          spend * CAPS.cycles[scenario],
        ),
        invoices: Math.min(
          invoices * invoiceRate[scenario] * fragmentation,
          spend * CAPS.invoices[scenario],
        ),
        fraud:
          invoices *
          exceptionRate *
          EXCEPTION_PREVENTION[scenario] *
          COST_PER_EXCEPTION *
          fraudCoefficient,
      };

      const { displayed, scale } = scaleForDisplay(categories, spend * CAPS.total[scenario]);

      return {
        ...acc,
        [scenario]: displayed,
        raw: { ...acc.raw, [scenario]: categories },
        scale: { ...acc.scale, [scenario]: scale },
      };
    },
    { raw: {}, scale: {} },
  );
};

const formatMoney = value =>
  `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Formats a number input in place (thousand separators, optional prefix) and returns its value. */
const formatNumberInput = input => {
  const prefix = input.dataset.calcPrefix || '';
  const previous = input.value;
  const caret = input.selectionEnd === null ? previous.length : input.selectionEnd;
  // Count digits *and* the decimal separator: counting digits alone would park the caret
  // in front of a just-typed ".", so the next keystrokes would land in the whole part.
  const keptBeforeCaret = previous.slice(0, caret).replace(/[^\d.]/g, '').length;

  // Keep the digits and at most one decimal separator. Stripping the separator outright
  // would turn a pasted "1,234.56" — the shape every spreadsheet and ERP export uses —
  // into 123,456, inflating the amount a hundredfold without any visible warning.
  const [rawWhole, ...rawFraction] = previous.replace(/[^\d.]/g, '').split('.');
  const hasSeparator = rawFraction.length > 0;
  const fraction = rawFraction.join('').slice(0, 2);
  const whole = rawWhole.replace(/^0+(?=\d)/, '').slice(0, MAX_DIGITS);
  const value = whole || fraction ? Number(`${whole || '0'}.${fraction || '0'}`) : 0;

  input.value =
    whole || hasSeparator
      ? `${prefix}${Number(whole || '0').toLocaleString('en-US')}${hasSeparator ? `.${fraction}` : ''}`
      : '';

  let seen = 0;
  let position = keptBeforeCaret === 0 ? prefix.length : input.value.length;

  if (keptBeforeCaret > 0) {
    for (let i = 0; i < input.value.length; i += 1) {
      if (/[\d.]/.test(input.value[i])) {
        seen += 1;
        if (seen === keptBeforeCaret) {
          position = i + 1;
          break;
        }
      }
    }
  }

  input.setSelectionRange(position, position);

  return value;
};

const TOOLTIP_GAP = 8; // px between the info icon and the tooltip
const VIEWPORT_PADDING = 16; // px the tooltip keeps from the viewport edges

/** Info tooltips: hover and keyboard focus on desktop, tap on touch devices. */
const initTooltips = section => {
  const tooltips = [...section.querySelectorAll('[data-tooltip]')]
    .map(tooltip => ({
      trigger: tooltip.querySelector('[data-tooltip-trigger]'),
      body: tooltip.querySelector('[data-tooltip-body]'),
    }))
    .filter(({ trigger, body }) => trigger && body);

  // The results panel scrolls and the section is animated with a transform, both of
  // which would trap or clip a positioned tooltip. Moving the bodies to <body> keeps
  // them anchored to the viewport instead.
  tooltips.forEach(({ body }) => document.body.appendChild(body));

  const hideAll = except => {
    tooltips.forEach(({ body }) => {
      if (body !== except) body.classList.remove('active');
    });
  };

  const place = ({ trigger, body }) => {
    const anchor = trigger.getBoundingClientRect();
    const { width, height } = body.getBoundingClientRect();
    const maxLeft = Math.max(window.innerWidth - width - VIEWPORT_PADDING, VIEWPORT_PADDING);
    const left = clamp(anchor.left + anchor.width / 2 - width / 2, VIEWPORT_PADDING, maxLeft);
    // Below the icon, as designed; flipped above only when there is no room left.
    const fitsBelow = anchor.bottom + TOOLTIP_GAP + height < window.innerHeight - VIEWPORT_PADDING;
    const top = fitsBelow
      ? anchor.bottom + TOOLTIP_GAP
      : Math.max(anchor.top - height - TOOLTIP_GAP, VIEWPORT_PADDING);

    body.style.left = `${left}px`;
    body.style.top = `${top}px`;
  };

  const show = tooltip => {
    hideAll(tooltip.body);
    place(tooltip);
    tooltip.body.classList.add('active');
  };

  tooltips.forEach(tooltip => {
    const { trigger, body } = tooltip;

    trigger.addEventListener('mouseenter', () => show(tooltip));
    // A tap or mouse press also focuses the button. Reacting to every focus would open the
    // tooltip before the click handler below runs and immediately toggles it shut again,
    // so only keyboard focus opens it here.
    trigger.addEventListener('focus', () => {
      if (trigger.matches(':focus-visible')) show(tooltip);
    });
    trigger.addEventListener('mouseleave', () => body.classList.remove('active'));
    trigger.addEventListener('blur', () => body.classList.remove('active'));
    trigger.addEventListener('click', event => {
      event.preventDefault();
      event.stopPropagation();
      if (body.classList.contains('active')) {
        body.classList.remove('active');
      } else {
        show(tooltip);
      }
    });
  });

  document.addEventListener('click', () => hideAll());
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') hideAll();
  });
  window.addEventListener('scroll', () => hideAll(), { passive: true });
  window.addEventListener('resize', () => hideAll());
};

/** Custom single-select dropdown, styled like the rest of the CMS filters. */
const initSelect = (element, onChange) => {
  const trigger = element.querySelector('[data-select-trigger]');
  const list = element.querySelector('[data-select-list]');
  const options = [...element.querySelectorAll('[data-select-option]')];
  const valueLabel = element.querySelector('[data-select-value]');
  const { calcSelect: field } = element.dataset;

  const setOpen = state => {
    trigger.dataset.state = state ? 'open' : 'closed';
    trigger.setAttribute('aria-expanded', String(state));
    list.classList.toggle('open', state);
  };

  const setValue = (value, silent) => {
    const option = options.find(item => item.dataset.value === value);
    if (!option) return;

    options.forEach(item => {
      item.classList.toggle('selected', item === option);
      item.setAttribute('aria-selected', String(item === option));
    });

    valueLabel.textContent = option.textContent.trim();
    trigger.classList.add('has-value');
    setOpen(false);

    if (!silent) onChange(field, value);
  };

  trigger.addEventListener('click', event => {
    event.stopPropagation();
    setOpen(trigger.dataset.state !== 'open');
  });

  options.forEach(option => {
    option.addEventListener('click', () => setValue(option.dataset.value));
  });

  document.addEventListener('click', event => {
    if (!element.contains(event.target)) setOpen(false);
  });

  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') setOpen(false);
  });

  return { field, element, setValue };
};

export const init = section => {
  // initTooltips() moves nodes out of the section, so a second run would half-initialise it.
  if (section.dataset.calcReady) return;
  section.dataset.calcReady = 'true';

  // Everything starts empty — the results stay at zero until the form is filled in.
  const state = {
    spend: 0,
    orders: 0,
    invoices: 0,
    employees: '',
    locations: '',
    process: '',
  };

  let scenario = 'min';
  let results = emptyResults();

  const resultNodes = RESULT_FIELDS.reduce((acc, field) => {
    const node = section.querySelector(`[data-calc-result="${field}"]`);
    return node ? { ...acc, [field]: node } : acc;
  }, {});

  const render = () => {
    Object.entries(resultNodes).forEach(([field, node]) => {
      node.textContent = formatMoney(results[scenario][field]);
    });
  };

  const update = () => {
    results = calculate(state);
    render();
  };

  // Number inputs
  section.querySelectorAll('[data-calc-number]').forEach(input => {
    input.addEventListener('input', () => {
      state[input.dataset.calcNumber] = formatNumberInput(input);
      input.classList.toggle('filled', input.value.length > 0);
      update();
    });
  });

  // Stepped slider (locations on ≥768px)
  const locationRadios = [...section.querySelectorAll('[data-calc-radio="locations"]')];
  const sliderValue = section.querySelector('[data-slider-value]');
  let selects = [];

  const setSliderValue = value => {
    const radio = locationRadios.find(item => item.value === value);
    if (!radio) return;
    radio.checked = true;
    if (sliderValue) {
      const label = radio.parentElement.querySelector('span:last-child');
      sliderValue.textContent = label ? label.textContent.trim() : value;
    }
  };

  // The slider and the ≤767px dropdown edit the same value — keep them in sync.
  const syncLocations = (value, source) => {
    setSliderValue(value);
    selects
      .filter(select => select.field === 'locations' && select.element !== source)
      .forEach(select => select.setValue(value, true));
  };

  // Dropdowns (employees, and locations on ≤767px)
  selects = [...section.querySelectorAll('[data-calc-select]')].map(element =>
    initSelect(element, (field, value) => {
      state[field] = value;
      if (field === 'locations') syncLocations(value, element);
      update();
    }),
  );

  locationRadios.forEach(radio => {
    radio.addEventListener('change', () => {
      if (!radio.checked) return;
      state.locations = radio.value;
      syncLocations(radio.value, radio);
      update();
    });
  });

  // Current purchasing process
  section.querySelectorAll('[data-calc-radio="process"]').forEach(radio => {
    radio.addEventListener('change', () => {
      if (!radio.checked) return;
      state.process = radio.value;
      update();
    });
  });

  // Minimum / maximum return tabs
  const tabs = [...section.querySelectorAll('[data-calc-tab]')];

  tabs.forEach(tab => {
    tab.addEventListener('click', () => {
      scenario = tab.dataset.calcTab;
      tabs.forEach(item => item.classList.toggle('active', item === tab));
      render();
    });
  });

  initTooltips(section);
  update();
};
